import { markCueSeen, readCueIds } from '../story/playback';
import { STORY_SECTIONS, readingSection, sectionProgress, sectionTitle, type StorySection, type StoryProgress } from '../story/sections';
import { STORY_BY_ID, STORY_EDITION, STORY_EPISODES, STORY_TRACKS, STORY_MOMENT_LABELS, storyBgmOf, type StoryEpisode } from '@hyakki/core/data/stories';
import { bgm, storyBgmKey } from '../audio';
import { go, register } from '../router';
import { esc } from '../ui/dom';
import { actorHtml, speakerName, storyBackground, storyCastForLine } from '../story/actors';
import { reviewAvailable } from '../story/review';
import { readLocal, writeLocal } from '../story/storage';
import { openSkipDialog } from '../story/skip';
import { STORY_SUMMARIES } from '../story/summaries';
import { state, subscribe } from '../state';

// 物語：読んだ場面（readCueIds）だけを読み返せる本編の一覧。読み合わせサーバーでは本編の全話（未読も）。外伝は公開前なので出さない。台詞へのメモ（開発用）は外した。
// 全面改稿前の読了状態を持ち込まない。旧版の読書位置・下書き・メモは削除しない。
const PROGRESS = `hk.story.progress.${STORY_EDITION}`;
const LAST = `hk.story.last.${STORY_EDITION}`;
const jump = (id: string, at: string) => go('story', { id, at });
const openSection = (s: StorySection) => go('story', { id: s.episode.id, at: s.lines[0].id, ...(s.cue ? { cue: s.cue.id } : {}) });

register('stories', {
  title: '物語', full: true,
  mount(el, params) {
    let off: (() => void) | undefined;
    let gone = false;
    let unsub: (() => void) | undefined;
    void reviewAvailable().then((review) => {
      if (gone) return;
      off = mountLibrary(el, params, review);
      // 読んだ場面は進み具合（ログイン情報）からも割り出すので、届いたら描き直す
      unsub = subscribe((ch) => {
        if (!ch.has('me') && !ch.has('profile') && !ch.has('progress')) return;
        off?.();
        off = mountLibrary(el, params, review);
      });
    });
    return () => { gone = true; unsub?.(); off?.(); };
  },
});

/** 公開している物語の種類。外伝は公開前なので、読み合わせサーバーでも出さない */
const PUBLIC_TRACKS = STORY_TRACKS.filter(t => t.id === 'main');

/** 読み返せる場面か（読み合わせサーバーでは本編を全部） */
const readable = (review: boolean) => {
  const read = review ? null : readCueIds();
  return (s: StorySection) => PUBLIC_TRACKS.some(t => t.id === s.episode.track) && (!read || (!!s.cue && read.has(s.cue.id)));
};

function mountLibrary(el: HTMLElement, params: URLSearchParams, review: boolean) {
  {
    const allowed = readable(review);
    const track = (review && PUBLIC_TRACKS.find(t => t.id === params.get('track'))) || PUBLIC_TRACKS[0];
    const episodes = STORY_EPISODES.filter(s => s.track === track.id && STORY_SECTIONS.some(part => part.episode.id === s.id && allowed(part)));
    const firstSection = STORY_SECTIONS.find(s => s.episode.track === track.id && allowed(s));
    const progress = readLocal<StoryProgress>(PROGRESS, {});
    const last = readLocal<{ id?: string; at?: string }>(LAST, {});
    const lastSection = last.id ? readingSection(last.id, null, last.at) : undefined;
    const resume = last.id && lastSection && allowed(lastSection) ? STORY_BY_ID[last.id] : undefined;
    const card = (s: StorySection) => {
      const p = sectionProgress(progress, s);
      return `<button class="story-chapter" data-section="${s.id}"><span class="story-chapter-no">${s.number}</span><span class="story-chapter-copy"><strong>${esc(s.title)}</strong><small>${esc(s.cue ? s.lines[0].scene.location : s.region)}</small></span><span class="story-read-state">${review ? (p?.complete ? '読了' : p?.lineId ? '途中' : '未読') : ''}<b>›</b></span></button>`;
    };
    const chapterList = track.id === 'main' ? episodes.map(s => {
      const sections = STORY_SECTIONS.filter(part => part.episode.id === s.id && allowed(part));
      const chapter = Number(s.id.split('-')[1]);
      const heading = chapter === 0 ? '序章' : chapter === 11 ? '終章' : `第${chapter}章 · ${s.location}`;
      const regions = [...new Set(sections.map(part => part.region))];
      return `<section class="story-country" aria-label="${esc(heading)}"><header><h3>${esc(heading)}</h3><p>${esc(s.title.replace(/^\S+\s+/, ''))}</p></header><div class="story-region-grid">${regions.map(region => `<div class="story-region"><h4>${esc(region)}</h4><div class="story-region-parts">${sections.filter(part => part.region === region).map(card).join('')}</div></div>`).join('')}</div></section>`;
    }).join('') : `<div class="story-chapters">${STORY_SECTIONS.filter(s => s.episode.track === track.id && allowed(s)).map(card).join('')}</div>`;
    el.innerHTML = `<div class="story-library">
      <header class="story-library-head"><button class="story-back" data-back aria-label="TOPへ">‹ TOP</button></header>
      <section class="story-cover" style="--story-bg:url('${storyBackground('village')}')">
        <div class="story-cover-art" aria-hidden="true">${actorHtml(track.char ?? 'akari', true)}${track.char ? '' : actorHtml('kazamaru', true)}</div>
        <div class="story-cover-copy"><span class="story-eyebrow">${track.id === 'main' ? (review ? '転生編 · 十国の物語＋序章・終章' : '十国の物語') : review ? `${track.label} · 全五話` : track.label}</span><h1>${esc(track.title)}</h1><p>${esc(track.description)}</p><div class="story-cover-actions">${firstSection ? `<button class="story-btn primary" data-section="${firstSection.id}">最初から読む <span>→</span></button>` : ''}${resume ? `<button class="story-btn" data-resume>続きから読む</button>` : ''}</div></div>
      </section>
      <div class="story-shelf">${review && PUBLIC_TRACKS.length > 1 ? `<nav class="story-tracks" aria-label="物語の種類">${PUBLIC_TRACKS.map(t => `<a href="#/stories?track=${t.id}" ${t.id === track.id ? 'aria-current="page"' : ''}>${t.label}</a>`).join('')}</nav>` : ''}
        <div class="story-list-heading"><h2>${track.label}</h2><span>${track.id === 'main' ? `${STORY_SECTIONS.filter(s => s.episode.track === 'main' && allowed(s)).length}/43場面` : review ? '新稿 · 魔王と四天王' : '魔王と四天王'}</span></div>
        ${review && track.id === 'main' ? '<p class="story-structure">1国＝1章、各章は4場面です。<br><b>前線地方：入国 → 砦攻略後</b><br><b>本城地方：本城戦前 → 国制圧後</b><br>番号は「章−場面」。各場面を個別に読めます。</p>' : ''}
        ${chapterList || '<p class="story-library-foot">まだ読んだ物語はありません。</p>'}
      </div></div>`;
    let closePanel: (() => void) | undefined;
    el.addEventListener('click', click);
    function click(e: MouseEvent) {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
      if (!b) return;
      if (b.dataset.section) { const section = STORY_SECTIONS.find(s => s.id === b.dataset.section); if (section) openSection(section); }
      if (b.hasAttribute('data-resume') && resume) jump(resume.id, last.at ?? resume.lines[0].id);
      if (b.hasAttribute('data-back')) return void go('top');
    }
    return () => { closePanel?.(); el.removeEventListener('click', click); };
  }
}

register('story', {
  title: '物語を読む', full: true,
  mount(el, params) {
    const story = STORY_BY_ID[params.get('id') ?? 'main-00'];
    if (!story) {
      el.innerHTML = '<div class="story-missing"><h1>この物語は見つかりませんでした</h1><a class="story-btn" href="#/stories">物語に戻る</a></div>';
      return;
    }
    // ゲーム中の場面の再生はどこでも。物語から開くのは読んだ場面だけ（読み合わせサーバーでは全部）
    const inGame = !!params.get('cue') && ['battle', 'result'].includes(params.get('back') ?? '');
    if (inGame) return mountReader(el, story, params, () => false);
    let off: (() => void) | undefined;
    let gone = false;
    void reviewAvailable().then((review) => {
      if (gone) return;
      const allowed = readable(review);
      if (!allowed(readingSection(story.id, params.get('cue'), params.get('at'))!)) return go('stories', {}, true);
      off = mountReader(el, story, params, allowed);
    });
    return () => { gone = true; off?.(); };
  },
});

function mountReader(el: HTMLElement, fullStory: StoryEpisode, params: URLSearchParams, allowed: (s: StorySection) => boolean) {
  const progress = readLocal<StoryProgress>(PROGRESS, {});
  const section = readingSection(fullStory.id, params.get('cue'), params.get('at') ?? progress?.[fullStory.id]?.lineId)!;
  const cue = section.cue;
  const destination = params.get('back');
  const inGame = !!cue && params.get('cue') === cue.id && (destination === 'battle' || destination === 'result');
  const story = { ...fullStory, lines: section.lines };
  const title = sectionTitle(section);
  const returnToGame = () => {
    if (!cue || !inGame) return;
    markCueSeen(cue);
    if (destination === 'battle') go('battle', { stage: params.get('stage') ?? '1', char: params.get('char') ?? state.oshi, diff: params.get('diff') ?? '0', ...(params.get('autorun') === '1' ? { autorun: '1' } : {}) }, true);
    else go('result', {}, true);
  };
  const target = params.get('at') ?? sectionProgress(progress, section)?.lineId;
  let index = Math.max(0, story.lines.findIndex(l => l.id === target));
  let auto = false, timer = 0, ending = false, dialogOpen = false;
  let closePanel: (() => void) | undefined;
  const track = STORY_TRACKS.find(t => t.id === story.track)!;
  const siblings = STORY_SECTIONS.filter(s => s.episode.track === story.track && (s === section || allowed(s)));
  const next = inGame ? undefined : siblings[siblings.indexOf(section) + 1];
  const dispose = new AbortController();
  el.innerHTML = `<article class="story-reader" style="--story-bg:url('${storyBackground(story.background)}')">
    <header class="story-reader-head">${inGame ? '' : `<a class="story-btn story-back" href="#/stories?track=${story.track}" aria-label="物語の一覧に戻る">‹ <span>物語</span></a>`}<div><small>${esc(cue ? fullStory.title : track.label)}</small><h1>${esc(title)}</h1></div><button class="story-btn" data-action="log">履歴</button></header>
    <div class="story-stage"><div class="story-location">${esc(story.location)}</div><div class="story-cast" aria-hidden="true"></div><div class="story-ending" hidden></div></div>
    <div class="story-dialogue-wrap"><button class="story-btn story-skip" data-action="skip">スキップ <b>»</b></button><div class="story-speaker"></div><button class="story-dialogue" data-action="next" aria-label="次の台詞へ"><span class="story-line-text" aria-live="polite"></span><span class="story-tap" aria-hidden="true">タップで次へ <b>▸</b></span></button></div>
    <footer class="story-controls"><button class="story-btn" data-action="prev" aria-label="前の台詞へ">‹ 戻る</button><span class="story-counter"></span><button class="story-btn" data-action="auto" aria-pressed="false">自動</button></footer>
    <div class="story-progress" role="progressbar" aria-label="この場面の進行" aria-valuemin="0" aria-valuemax="100"><i></i></div>
  </article>`;
  const $ = <T extends HTMLElement>(s: string) => el.querySelector<T>(s)!;
  const persist = (complete = false) => {
    const all = readLocal<StoryProgress>(PROGRESS, {});
    const safe = all && typeof all === 'object' && !Array.isArray(all) ? all : {};
    safe[section.id] = { lineId: story.lines[index].id, complete: complete || !!sectionProgress(safe, section)?.complete };
    writeLocal(PROGRESS, safe); writeLocal(LAST, { id: story.id, at: story.lines[index].id });
  };
  function stopAuto() { auto = false; clearTimeout(timer); $<HTMLButtonElement>('[data-action="auto"]').setAttribute('aria-pressed', 'false'); }
  function schedule() {
    clearTimeout(timer);
    if (auto && !dialogOpen && !ending && !document.hidden) timer = window.setTimeout(advance, Math.max(3200, 1500 + story.lines[index].text.length * 90));
  }
  function render() {
    const line = story.lines[index];
    const cast = storyCastForLine(line);
    // 曲は行ごとに場面の曲へ（同じ曲なら何もしない。戻る・履歴から飛んだときも合う）
    void bgm(storyBgmKey(storyBgmOf(fullStory, line)));
    $('.story-reader').style.setProperty('--story-bg', `url('${storyBackground(line.scene.background)}')`);
    $('.story-location').textContent = `${line.moment ? STORY_MOMENT_LABELS[line.moment] + '｜' : ''}${line.scene.location}`;
    $('.story-cast').innerHTML = cast.map(id => actorHtml(id, id === line.speaker || line.speaker === 'narrator')).join('');
    $('.story-speaker').textContent = speakerName(line.speaker, state.me?.user);
    $('.story-speaker').dataset.speaker = line.speaker;
    $('.story-dialogue-wrap').classList.toggle('is-narration', line.speaker === 'narrator');
    $('.story-line-text').textContent = line.text;
    $('.story-counter').textContent = `${index + 1} / ${story.lines.length}`;
    $<HTMLButtonElement>('[data-action="prev"]').disabled = index === 0 && !ending;
    $('.story-ending').hidden = !ending;
    const pct = Math.round((index + 1) / story.lines.length * 100);
    $('.story-progress').setAttribute('aria-valuenow', String(pct));
    $('.story-progress i').style.width = `${pct}%`;
    document.title = `${title}｜百鬼国盗`;
    const url = `#/story?${new URLSearchParams({ ...(inGame ? Object.fromEntries(params) : {}), id: story.id, at: line.id, ...(cue ? { cue: cue.id } : {}) })}`;
    history.replaceState(null, '', url);
    persist(); schedule();
  }
  function advance() {
    if (dialogOpen || ending) return;
    if (index < story.lines.length - 1) { index++; render(); return; }
    if (inGame) { stopAuto(); persist(true); returnToGame(); return; }
    ending = true; stopAuto(); persist(true);
    $('.story-ending').innerHTML = `<span class="story-eyebrow">${esc(title)}</span><h2>${cue ? 'この場面を読み終えました' : 'この話を読み終えました'}</h2>${next ? `<p>次：${esc(sectionTitle(next))}<br>${esc(next.region)}</p>` : ''}${next ? `<button class="story-btn primary" data-action="next-episode">${cue ? '次の場面へ' : '次の話へ'} →</button>` : ''}<a class="story-btn" href="#/stories?track=${story.track}">一覧へ</a>`;
    render();
  }
  function openLog() {
    stopAuto(); dialogOpen = true;
    const dialog = document.createElement('dialog'); dialog.className = 'story-dialog story-log'; dialog.setAttribute('aria-label', '会話の履歴');
    dialog.innerHTML = `<header><h2>会話の履歴</h2><button class="story-btn" data-close>閉じる ×</button></header><div class="story-log-scroll">${story.lines.slice(0, index + 1).map((l, i) => `<button data-line="${i}" class="${l.speaker === 'narrator' ? 'is-narration' : ''}"><small><span data-speaker="${l.speaker}">${esc(speakerName(l.speaker, state.me?.user))}</span><span>${i + 1}</span></small><span>${esc(l.text)}</span></button>`).join('')}</div>`;
    document.body.appendChild(dialog);
    const unsubscribe = subscribe(changed => {
      if (changed.has('me')) for (const label of dialog.querySelectorAll<HTMLElement>('[data-speaker="player"]')) label.textContent = speakerName('player', state.me?.user);
    });
    const close = () => { unsubscribe(); dialog.close(); dialog.remove(); dialogOpen = false; closePanel = undefined; };
    closePanel = close;
    dialog.addEventListener('cancel', e => { e.preventDefault(); close(); });
    dialog.addEventListener('click', e => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
      if (b?.hasAttribute('data-close')) close();
      else if (b?.dataset.line) { index = Number(b.dataset.line); ending = false; close(); render(); }
    });
    dialog.showModal();
    const scroller = dialog.querySelector('.story-log-scroll')!; scroller.scrollTop = scroller.scrollHeight;
  }
  /** スキップ：この場面のあらすじを見せてから確かめる（読まなくても話の流れがわかるように）。ゲーム中は戦いへ、物語から読んでいるときは読み終わりへ */
  function openSkip() {
    if (ending) return;
    const done = () => {
      if (inGame) return returnToGame();
      index = story.lines.length - 1;
      advance();
    };
    const summary = cue ? STORY_SUMMARIES[cue.id] : undefined;
    if (!summary) return done();
    dialogOpen = true;
    closePanel = openSkipDialog({ eyebrow: fullStory.title, title, summary, skip: done, closed: () => { dialogOpen = false; closePanel = undefined; } });
  }
  el.addEventListener('click', e => {
    const t = e.target as HTMLElement;
    const b = t.closest<HTMLButtonElement>('[data-action]');
    if (dialogOpen) return;
    // 会話の枠の外（背景・立ち絵）を押しても次へ。上の見出し・下の操作・読み終わりの札・ほかのボタンやリンクは除く
    if (!b) {
      if (!t.closest('.story-reader-head, .story-controls, .story-ending, button, a, input, textarea')) advance();
      return;
    }
    switch (b.dataset.action) {
      case 'next': advance(); break;
      case 'skip': stopAuto(); openSkip(); break;
      case 'prev': stopAuto(); if (ending) ending = false; else index = Math.max(0, index - 1); render(); break;
      case 'auto': auto = !auto; b.setAttribute('aria-pressed', String(auto)); schedule(); break;
      case 'log': openLog(); break;
      case 'next-episode': if (next) openSection(next); break;
    }
  }, { signal: dispose.signal });
  document.addEventListener('keydown', e => {
    if (dialogOpen || e.ctrlKey || e.altKey || e.metaKey || (e.target as HTMLElement).closest('button, a, input, textarea, select')) return;
    if (['ArrowRight', ' ', 'Enter'].includes(e.key)) { e.preventDefault(); advance(); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); stopAuto(); if (ending) ending = false; else index = Math.max(0, index - 1); render(); }
  }, { signal: dispose.signal });
  document.addEventListener('visibilitychange', () => { if (document.hidden) stopAuto(); }, { signal: dispose.signal });
  render();
  const unsubscribe = subscribe(changed => {
    if (changed.has('me')) $('.story-speaker').textContent = speakerName(story.lines[index].speaker, state.me?.user);
  });
  // 次の話の背景を先読み。画像生成や外部サービスへの依存はない。
  if (next) { const img = new Image(); img.src = storyBackground(next.lines[0].scene.background); }
  return () => { unsubscribe(); clearTimeout(timer); dispose.abort(); closePanel?.(); };
}
